<script lang="ts">
    import {getInjector} from 'src/core';
    import type {Auth} from 'src/typings';
    import type {MenuItem} from 'shared/components/kp-main-menu/types';
    import {KpMainMenuService} from 'shared/components/kp-main-menu/kp-main-menu.service';
    import {fade} from 'svelte/transition';
    import {cleanup, exists, ignoreUnusedProperties} from 'shared/utils/custom-utils';
    import {createMainMenuContext} from 'shared/components/kp-main-menu/main-menu-context';
    import {onDestroy} from 'svelte';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';
    import KpSideMainMenu from 'shared/components/kp-main-menu/side-menu/KpSideMainMenu.svelte';
    import KpMainMenuNav from 'shared/components/kp-main-menu/menu-nav/KpMainMenuNav.svelte';
    import KpMainMenuNavItem from 'shared/components/kp-main-menu/menu-nav/KpMainMenuNavItem.svelte';
    import KpMainMenuSearch from 'shared/components/kp-main-menu/menu-search/KpMainMenuSearch.svelte';

    const service = getInjector().getByClass(KpMainMenuService);
    const context = createMainMenuContext(service);
    const forceShowsSearch = service.getMainMenuForceShowSearch();

    let userMenuItem: MenuItem;
    let mainMenuElement: HTMLElement;
    let mobileSideMenuOpen = false;
    let windowScrollY = 0;
    let isStuckToTop = false;
    let isSearchOpen = false;
    const userMenuItemUnsubscribe = context.items.subscribe((currentItems) => userMenuItem = currentItems.find((item) => item.type === 'user'));
    const isStuckToTopUnsubscribe = context.isStuckToTop.subscribe((currentIsStuckToTop) => isStuckToTop = currentIsStuckToTop);
    const isSearchOpenUnsubscribe = context.isNavSearchOpen.subscribe((currentIsSearchOpen) => isSearchOpen = currentIsSearchOpen);
    $: calculateIsStuckToTop(windowScrollY);

    let currentAuth: Auth;
    const currentAuthSubscription = service.getCurrentAuth$().subscribe((auth) => {
        currentAuth = auth;
        service.getMenuItems().then((menuItems) => context.updateMenuItems(menuItems));
    });

    onDestroy(() => {
        cleanup(currentAuthSubscription, isSearchOpenUnsubscribe, userMenuItemUnsubscribe, isStuckToTopUnsubscribe);
    });

    function calculateIsStuckToTop(scrollY: number) {
        // We only use scrollY for reactivity to trigger
        // isStuckToTop recalculation every time them scroll
        // position is changed, so we can ignore it
        ignoreUnusedProperties(scrollY);

        if (!exists(mainMenuElement)) {
            return;
        }

        const rect = mainMenuElement.getBoundingClientRect();
        context.updateIsStuckToTop(rect.top);
    }

    const handleCloseSideMenu = () => {
        mobileSideMenuOpen = false;
    };
</script>

<svelte:window bind:scrollY={windowScrollY}/>

<nav class="kp-main-menu"
     style="--main-menu-background-color: {context.backgroundColor}; --main-menu-text-color: {context.textColor}; --main-menu-highlight-background-color: {context.highlightBackgroundColor}; --main-menu-highlight-text-color: {context.highlightTextColor};"
     bind:this={mainMenuElement}
     class:stuck-to-top={isStuckToTop}>

    <div class="menu-container container">
        <button type="button"
                class="side-menu-btn"
                aria-label="Menu"
                aria-haspopup="menu"
                aria-expanded={mobileSideMenuOpen}
                on:click={() => mobileSideMenuOpen = !mobileSideMenuOpen}>
            <UIcon icon="menu-burger"/>
        </button>

        {#if !isSearchOpen}
            <KpMainMenuNav/>
        {/if}

        {#if isStuckToTop || $forceShowsSearch}
            <KpMainMenuSearch/>
        {/if}

        {#if exists(userMenuItem)}
            <ul class="user-menu"
                aria-label="{!currentAuth.evided ? '' : `Přihlášený uživatel ${userMenuItem.text}`}">
                <KpMainMenuNavItem additionalClasses="user-nav-item"
                                   dataQaPrefix="user-menu-item"
                                   menuItem={userMenuItem}
                                   icon={!currentAuth.evided ? 'sign-in-alt' : 'user'}/>
            </ul>
        {/if}
    </div>
</nav>

<div class="mobile-side-menu-container" class:open={mobileSideMenuOpen}>
    <KpSideMainMenu isOpened={mobileSideMenuOpen}
                    on:click-outside={handleCloseSideMenu}
                    on:link-click={handleCloseSideMenu}/>
</div>

{#if mobileSideMenuOpen}
    <div class="dark-overlay" transition:fade={{duration: 250}}></div>
{/if}

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    @main-menu-height: 48px;

    .kp-main-menu {
        height: @main-menu-height;
        background-color: var(--main-menu-background-color);
        color: var(--main-menu-text-color);
        font-weight: 500;
        display: flex;
        white-space: nowrap;
        border-bottom: 1px solid @themed-border-default;

        .menu-container {
            justify-content: center;
            display: flex;
            overflow-x: auto;
            overflow-y: hidden;

            .side-menu-btn {
                margin-right: auto;
                margin-left: 8px;
                border: none;
                background: none;
                outline: none;
                color: inherit;
                cursor: pointer;
            }

            .user-menu {
                margin-left: @spacing-sm;
            }
        }
    }

    .mobile-side-menu-container {
        position: fixed;
        z-index: 100;
        top: 0;
        left: 0;
        bottom: 0;
        height: 100%;
        display: flex;
        transform: translateX(-100%);
        transition: transform 0.3s ease-in-out;

        &.open {
            transform: translateX(0);
        }
    }

    .dark-overlay {
        position: fixed;
        z-index: 10;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.25);
    }

    @media screen and (min-width: 721px) {
        .kp-main-menu .menu-container .side-menu-btn {
            display: none;
        }

        .mobile-side-menu-container {
            display: none;
        }
    }

    @media screen and (max-width: 720px) {
        .kp-main-menu .menu-container {
            width: 100%;
            justify-content: space-between;
        }
    }

    :global {
        .kp-main-menu .warning-chip {
            // Override kp-chip-tag styles to make it into a circle
            width: 20px !important;
            height: 20px !important;
            padding: 0 !important;
            display: inline-flex;
            align-items: center;
            justify-content: center;
        }
    }
</style>